/**
 * Lift2Fit — the coach's at-a-glance read of the person they are chatting with
 * (`#/chat`, 0028 IA follow-up).
 *
 * Deliberately a THIRD PANEL reusing an existing read, not a new endpoint:
 * every fact here comes from `GET /coaching/clients` — the same roster row the
 * Clients page renders. The API already gates that row by the link's
 * `shared_fields` (session facts follow `sessions`, set facts follow `sets`) and
 * derives the block from the coach's OWN programme, so this panel cannot expose
 * anything the client has not shared. The workspace fetches the roster once,
 * lazily, on the first thread a coach selects; this component only renders.
 *
 * It degrades honestly: a prospect is not a client, so they have no roster row
 * and the panel says nothing is shared rather than showing a row of zeros.
 */
import type { ConnectionTier, Trainee } from '@/lib/api';
import { formatDate, timeAgo } from '@/lib/format';
import { AdherenceBar, FlagChips, Panel, StatusChip } from '../components';
import { formatDateShort, managedBySentence } from './shared';

export function ChatContextPanel({
  displayName,
  tier,
  row,
  loading,
  error,
}: {
  displayName: string;
  tier: ConnectionTier;
  row: Trainee | null;
  loading: boolean;
  error: string | null;
}) {
  const what = loading
    ? 'Reading their programme…'
    : error
      ? 'Their programme could not be read right now.'
      : row
        ? 'The programme you prescribe and the progress they share.'
        : 'Nothing shared yet.';

  return (
    <Panel
      shape="chat"
      testId="chat-context-panel"
      title="Their training"
      subtitle={what}
      className="min-h-0"
      bodyClassName="gap-3"
    >
      {!loading && !error && row === null ? (
        <p className="text-[13px] text-ink-muted" data-testid="chat-context-empty">
          {tier === 'inquiry'
            ? `${displayName} asked to connect, but nothing is shared yet — a programme and progress appear once you connect and they take a pack.`
            : tier === 'connected'
              ? `${displayName} is connected but not a client yet. Nothing is shared until they take one of your packs.`
              : `Nothing is shared with you right now.`}
        </p>
      ) : null}

      {row ? (
        <>
          <div className="flex flex-col gap-1" data-testid="chat-context-block">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Programme</span>
            {row.blockName ? (
              <>
                <span className="text-[14px] font-bold text-ink">{row.blockName}</span>
                <span className="text-[12px] text-ink-muted">
                  Week {row.weekOf ?? '—'} of {row.weeks ?? '—'}
                  {row.blockEndsOn ? ` · ends ${formatDateShort(row.blockEndsOn)}` : ''}
                </span>
                {row.reviewDue ? (
                  <span data-testid="chat-context-review">
                    <StatusChip tone="info">
                      {row.daysRemaining !== null && row.daysRemaining !== undefined && row.daysRemaining < 0
                        ? `Review overdue by ${Math.abs(row.daysRemaining)} day(s)`
                        : row.daysRemaining === 0
                          ? 'Review due — the block ends today'
                          : `Review due — ${row.daysRemaining ?? 0} day(s) left`}
                    </StatusChip>
                  </span>
                ) : null}
              </>
            ) : (
              <span className="text-[13px] text-ink-muted" data-testid="chat-context-no-block">
                No programme set yet.
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1" data-testid="chat-context-progress">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">Progress</span>
            {row.sharedFields.includes('sessions') ? (
              <span className="text-[13px] text-ink">
                {row.lastSessionAt ? `Last session ${timeAgo(row.lastSessionAt)}` : 'Never logged'}
                <span className="text-ink-muted"> · {row.sessionsLast7} session(s) / 7d</span>
              </span>
            ) : (
              <span className="text-[13px] text-ink-muted" data-testid="chat-context-progress-withheld">
                They have not shared their sessions.
              </span>
            )}
            {row.sharedFields.includes('sets') && row.compliancePct !== null ? (
              <span className="flex flex-wrap items-center gap-2" data-testid="chat-context-adherence">
                <AdherenceBar pct={row.compliancePct} />
                <span className="l2f-tabular font-mono text-[12px] font-bold text-ink">{row.compliancePct}%</span>
              </span>
            ) : (
              <span className="text-[12px] text-ink-muted" data-testid="chat-context-adherence-withheld">
                Adherence is not shared.
              </span>
            )}
          </div>

          {row.flags.length ? (
            <div className="flex flex-wrap items-center gap-2" data-testid="chat-context-flags">
              <FlagChips flags={row.flags} />
            </div>
          ) : null}

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-hairline pt-2">
            {/* 0030: who manages this client, from the reader's point of view. */}
            {row.managedBy ? (
              <span
                className="w-full text-[11px] font-bold uppercase tracking-[0.06em] text-accent"
                data-testid="chat-context-manager"
              >
                {managedBySentence(row.managedBy)}
              </span>
            ) : null}
            <span className="text-[11px] text-ink-muted">
              Sharing {row.sharedFields.length ? row.sharedFields.join(', ') : 'nothing'}
            </span>
            <a
              href={`#/crm/clients/${encodeURIComponent(row.userId)}`}
              target="_blank"
              rel="noopener"
              data-testid="chat-context-profile"
              title={`Open ${displayName}'s full client page in a new window`}
              className="text-[11px] font-bold uppercase tracking-[0.06em] text-accent hover:text-accent-hover"
            >
              Full page
            </a>
          </div>
          {row.linkedAt ? (
            <span className="text-[11px] text-ink-muted">Linked {formatDate(row.linkedAt)}</span>
          ) : null}
        </>
      ) : null}
    </Panel>
  );
}
